import * as WutiReact from 'react';
import * as WutiFrontendPlatform from '@edx/frontend-platform';

function addPlugins(config, slot_name, plugins) {
  if (slot_name in config.pluginSlots === false) {
    config.pluginSlots[slot_name] = {
      keepDefault: true,
      plugins: []
    };
  }

  config.pluginSlots[slot_name].plugins.push(...plugins);
}const WUTISKILL_THEME_LINK_ID = 'wutiskill-theme-tenant-css';
const WUTISKILL_COOKIE_EVENT = 'wutiskill:cookie-consent';
const WUTISKILL_COOKIE_MESSAGES = {
  en: {
    dialogLabel: 'Cookie preferences',
    title: 'Cookie preferences',
    copyBeforeLink: 'We use cookies that are necessary for the platform to work. With your consent, we can also measure usage and improve learning journeys. ',
    privacyLink: 'Privacy policy',
    necessaryTitle: 'Necessary',
    necessaryCopy: 'Required for sign-in, security, and navigation.',
    analyticsTitle: 'Analytics',
    analyticsCopy: 'Helps us understand course usage and improve the service.',
    marketingTitle: 'Communications',
    marketingCopy: 'Allows us to personalize selected training-related messages.',
    reject: 'Reject',
    customize: 'Customize',
    save: 'Save choices',
    acceptAll: 'Accept all',
  },
  fr: {
    dialogLabel: 'Préférences cookies',
    title: 'Gestion des cookies',
    copyBeforeLink: "Nous utilisons des cookies nécessaires au fonctionnement de la plateforme. Avec votre accord, nous pouvons aussi mesurer l'audience et améliorer les parcours de formation. ",
    privacyLink: 'Politique de confidentialité',
    necessaryTitle: 'Nécessaires',
    necessaryCopy: 'Indispensables pour la connexion, la sécurité et la navigation.',
    analyticsTitle: "Mesure d'audience",
    analyticsCopy: "Nous aide à comprendre l'utilisation des cours et à améliorer le service.",
    marketingTitle: 'Communication',
    marketingCopy: 'Permet de personnaliser certains messages liés aux formations.',
    reject: 'Refuser',
    customize: 'Personnaliser',
    save: 'Enregistrer mes choix',
    acceptAll: 'Tout accepter',
  },
};

const getWutiCookieMessages = () => {
  const config = WutiFrontendPlatform.getConfig();
  const pageLanguage = document.documentElement.getAttribute('lang');
  const locale = (config.DEFAULT_LANGUAGE || pageLanguage || navigator.language || 'en').toLowerCase();
  const language = locale.split('-')[0];

  return WUTISKILL_COOKIE_MESSAGES[language] || WUTISKILL_COOKIE_MESSAGES.en;
};

const getWutiCookieConsentStorageKey = () => {
  const version = WutiFrontendPlatform.getConfig().WUTISKILL_COOKIE_CONSENT_VERSION || 'v1';
  return `wutiskill_cookie_consent_${version}`;
};

const getWutiCookieConsent = () => {
  try {
    const rawConsent = window.localStorage.getItem(getWutiCookieConsentStorageKey());
    return rawConsent ? JSON.parse(rawConsent) : null;
  } catch (error) {
    return null;
  }
};

const saveWutiCookieConsent = (preferences) => {
  const consent = {
    necessary: true,
    analytics: Boolean(preferences.analytics),
    marketing: Boolean(preferences.marketing),
    updatedAt: new Date().toISOString(),
  };

  try {
    window.localStorage.setItem(getWutiCookieConsentStorageKey(), JSON.stringify(consent));
  } catch (error) {
    return consent;
  }

  window.dispatchEvent(new CustomEvent(WUTISKILL_COOKIE_EVENT, { detail: consent }));
  return consent;
};

const WutiTenantThemeLoader = () => {
  WutiReact.useEffect(() => {
    const config = WutiFrontendPlatform.getConfig();
    const themeCssUrl = config.WUTISKILL_THEME_CSS_URL;
    const themeName = config.WUTISKILL_THEME_NAME || 'wutiskill';

    document.documentElement.setAttribute('data-wuti-theme', themeName);

    if (!themeCssUrl) {
      return undefined;
    }

    let link = document.getElementById(WUTISKILL_THEME_LINK_ID);
    if (!link) {
      link = document.createElement('link');
      link.id = WUTISKILL_THEME_LINK_ID;
      link.rel = 'stylesheet';
      document.head.appendChild(link);
    }

    if (link.href !== themeCssUrl) {
      link.href = themeCssUrl;
    }

    return undefined;
  }, []);

  return null;
};

const WutiCookieConsent = () => {
  const config = WutiFrontendPlatform.getConfig();
  const enabled = config.WUTISKILL_COOKIE_CONSENT_ENABLED !== false;
  const privacyUrl = config.WUTISKILL_COOKIE_POLICY_URL || config.PRIVACY_POLICY || '/privacy';
  const messages = getWutiCookieMessages();
  const [consent, setConsent] = WutiReact.useState(() => getWutiCookieConsent());
  const [customizing, setCustomizing] = WutiReact.useState(false);
  const [analytics, setAnalytics] = WutiReact.useState(false);
  const [marketing, setMarketing] = WutiReact.useState(false);

  WutiReact.useEffect(() => {
    if (consent) {
      window.dispatchEvent(new CustomEvent(WUTISKILL_COOKIE_EVENT, { detail: consent }));
    }
  }, [consent]);

  if (!enabled || consent) {
    return null;
  }

  const save = (preferences) => {
    setConsent(saveWutiCookieConsent(preferences));
  };

  return (
    <div className="wuti-cookie-consent" role="dialog" aria-live="polite" aria-label={messages.dialogLabel}>
      <style>
        {`
          .wuti-cookie-consent {
            position: fixed;
            right: 24px;
            bottom: 24px;
            z-index: 2147483000;
            width: min(560px, calc(100vw - 32px));
            color: var(--wuti-color-text, #17172c);
            background: var(--wuti-color-surface, #ffffff);
            border: 1px solid var(--wuti-color-border, #d9d6cf);
            box-shadow: 0 24px 60px rgba(17, 17, 31, 0.18);
            border-radius: 8px;
            padding: 20px;
            font-family: Inter, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
          }

          .wuti-cookie-consent__title {
            margin: 0 0 8px;
            font-size: 18px;
            line-height: 1.25;
            font-weight: 700;
          }

          .wuti-cookie-consent__copy {
            margin: 0;
            color: var(--wuti-color-text-muted, #5f5d6f);
            font-size: 14px;
            line-height: 1.55;
          }

          .wuti-cookie-consent__link {
            color: var(--wuti-color-primary, #5b1028);
            font-weight: 700;
            text-decoration: underline;
            text-underline-offset: 3px;
          }

          .wuti-cookie-consent__choices {
            display: grid;
            gap: 10px;
            margin-top: 16px;
          }

          .wuti-cookie-consent__choice {
            display: flex;
            align-items: flex-start;
            gap: 10px;
            padding: 12px;
            border: 1px solid var(--wuti-color-border, #d9d6cf);
            border-radius: 6px;
            background: var(--wuti-color-surface-muted, #faf9f6);
          }

          .wuti-cookie-consent__choice input {
            margin-top: 4px;
          }

          .wuti-cookie-consent__choice strong {
            display: block;
            font-size: 14px;
            line-height: 1.35;
          }

          .wuti-cookie-consent__choice span {
            display: block;
            margin-top: 2px;
            color: var(--wuti-color-text-muted, #5f5d6f);
            font-size: 13px;
            line-height: 1.45;
          }

          .wuti-cookie-consent__actions {
            display: flex;
            flex-wrap: wrap;
            justify-content: flex-end;
            gap: 10px;
            margin-top: 18px;
          }

          .wuti-cookie-consent__button {
            min-height: 40px;
            padding: 9px 14px;
            border: 1px solid var(--wuti-color-border, #d9d6cf);
            border-radius: 6px;
            background: var(--wuti-color-surface, #ffffff);
            color: var(--wuti-color-text, #17172c);
            font: inherit;
            font-size: 14px;
            font-weight: 700;
            cursor: pointer;
          }

          .wuti-cookie-consent__button--primary {
            border-color: var(--wuti-color-action, #ffba08);
            background: var(--wuti-color-action, #ffba08);
            color: var(--wuti-color-on-action, #17172c);
          }

          .wuti-cookie-consent__button:focus-visible,
          .wuti-cookie-consent__link:focus-visible,
          .wuti-cookie-consent__choice input:focus-visible {
            outline: 3px solid rgba(255, 186, 8, 0.45);
            outline-offset: 2px;
          }

          @media (max-width: 640px) {
            .wuti-cookie-consent {
              right: 16px;
              bottom: 16px;
              padding: 16px;
            }

            .wuti-cookie-consent__actions {
              display: grid;
              grid-template-columns: 1fr;
            }
          }
        `}
      </style>
      <h2 className="wuti-cookie-consent__title">{messages.title}</h2>
      <p className="wuti-cookie-consent__copy">
        {messages.copyBeforeLink}
        <a className="wuti-cookie-consent__link" href={privacyUrl}>{messages.privacyLink}</a>
      </p>

      {customizing && (
        <div className="wuti-cookie-consent__choices">
          <label className="wuti-cookie-consent__choice">
            <input type="checkbox" checked disabled />
            <span>
              <strong>{messages.necessaryTitle}</strong>
              <span>{messages.necessaryCopy}</span>
            </span>
          </label>
          <label className="wuti-cookie-consent__choice">
            <input
              type="checkbox"
              checked={analytics}
              onChange={(event) => setAnalytics(event.target.checked)}
            />
            <span>
              <strong>{messages.analyticsTitle}</strong>
              <span>{messages.analyticsCopy}</span>
            </span>
          </label>
          <label className="wuti-cookie-consent__choice">
            <input
              type="checkbox"
              checked={marketing}
              onChange={(event) => setMarketing(event.target.checked)}
            />
            <span>
              <strong>{messages.marketingTitle}</strong>
              <span>{messages.marketingCopy}</span>
            </span>
          </label>
        </div>
      )}

      <div className="wuti-cookie-consent__actions">
        <button
          className="wuti-cookie-consent__button"
          type="button"
          onClick={() => save({ analytics: false, marketing: false })}
        >
          {messages.reject}
        </button>
        {!customizing && (
          <button
            className="wuti-cookie-consent__button"
            type="button"
            onClick={() => setCustomizing(true)}
          >
            {messages.customize}
          </button>
        )}
        {customizing ? (
          <button
            className="wuti-cookie-consent__button wuti-cookie-consent__button--primary"
            type="button"
            onClick={() => save({ analytics, marketing })}
          >
            {messages.save}
          </button>
        ) : (
          <button
            className="wuti-cookie-consent__button wuti-cookie-consent__button--primary"
            type="button"
            onClick={() => save({ analytics: true, marketing: true })}
          >
            {messages.acceptAll}
          </button>
        )}
      </div>
    </div>
  );
};

async function setConfig () {
  let config = {
    pluginSlots: {}
  };

  try {
    /* We can't assume FPF exists, as it's not declared as a dependency in all
     * MFEs, so we import it dynamically. In addition, for dynamic imports to
     * work with Webpack all of the code that actually uses the imported module
     * needs to be inside the `try{}` block.
     */
    const { DIRECT_PLUGIN, PLUGIN_OPERATIONS } = await import('@openedx/frontend-plugin-framework');
    const { NotificationsTray } = await import('@edx/frontend-plugin-notifications');
    addPlugins(config, 'org.openedx.frontend.layout.header_desktop_secondary_menu.v1', [
    {
      op: PLUGIN_OPERATIONS.Insert,
      widget: {
        id: 'notification-drawer-widget',
        priority: 10,
        type: DIRECT_PLUGIN,
        RenderWidget: NotificationsTray,
      },
    }
]);
    addPlugins(config, 'org.openedx.frontend.layout.header_learning_help.v1', [
    {
      op: PLUGIN_OPERATIONS.Insert,
      widget: {
        id: 'notification-drawer-widget',
        priority: 10,
        type: DIRECT_PLUGIN,
        RenderWidget: NotificationsTray,
      },
    }
]);
    addPlugins(config, 'org.openedx.frontend.layout.studio_header_search_button_slot.v1', [
    {
      op: PLUGIN_OPERATIONS.Insert,
      widget: {
        id: 'notification-drawer-widget',
        priority: 10,
        type: DIRECT_PLUGIN,
        RenderWidget: NotificationsTray,
      },
    }
]);
    if (process.env.APP_ID == 'admin-console') {
    }
    if (process.env.APP_ID == 'authn') {
    }
    if (process.env.APP_ID == 'authoring') {// wutiskill-ai: Import chat widget components for Authoring MFE
const { AIChatTray, CourseGenerationButton } = await import('@wutiskill/ai-chat-widget');

// wutiskill-ai: MFE env config for Authoring (Studio) MFE
window.WUTISKILL_AI_CONFIG = {
  ENABLE: 'true',
  ENABLE_CHAT: 'true',
  ENABLE_COURSE_CREATION: 'false',
  LMS_BASE_URL: 'https://wutilms.com',
  CMS_BASE_URL: 'https://studio.wutilms.com',
};
      addPlugins(config, 'org.openedx.frontend.layout.studio_footer.v1', [
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'wutiskill_ai_chat_widget_authoring',
                    priority: 50,
                    type: DIRECT_PLUGIN,
                    RenderWidget: AIChatTray,
                },
            }]);
      addPlugins(config, 'org.openedx.frontend.authoring.course_outline_header_actions.v1', [
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'wutiskill_ai_course_generation_button',
                    priority: 60,
                    type: DIRECT_PLUGIN,
                    RenderWidget: CourseGenerationButton,
                },
            }]);
      addPlugins(config, 'footer_slot', [
{
  op: PLUGIN_OPERATIONS.Insert,
  widget: {
    id: 'wutiskill_theme_loader',
    type: DIRECT_PLUGIN,
    priority: 100,
    RenderWidget: WutiTenantThemeLoader,
  },
}
]);
      addPlugins(config, 'footer_slot', [
{
  op: PLUGIN_OPERATIONS.Insert,
  widget: {
    id: 'wutiskill_cookie_consent',
    type: DIRECT_PLUGIN,
    priority: 101,
    RenderWidget: WutiCookieConsent,
  },
}
]);
    }
    if (process.env.APP_ID == 'account') {
      addPlugins(config, 'footer_slot', [
{
  op: PLUGIN_OPERATIONS.Insert,
  widget: {
    id: 'wutiskill_theme_loader',
    type: DIRECT_PLUGIN,
    priority: 100,
    RenderWidget: WutiTenantThemeLoader,
  },
}
]);
      addPlugins(config, 'footer_slot', [
{
  op: PLUGIN_OPERATIONS.Insert,
  widget: {
    id: 'wutiskill_cookie_consent',
    type: DIRECT_PLUGIN,
    priority: 101,
    RenderWidget: WutiCookieConsent,
  },
}
]);
    }
    if (process.env.APP_ID == 'communications') {
    }
    if (process.env.APP_ID == 'discussions') {
      addPlugins(config, 'footer_slot', [
{
  op: PLUGIN_OPERATIONS.Insert,
  widget: {
    id: 'wutiskill_theme_loader',
    type: DIRECT_PLUGIN,
    priority: 100,
    RenderWidget: WutiTenantThemeLoader,
  },
}
]);
      addPlugins(config, 'footer_slot', [
{
  op: PLUGIN_OPERATIONS.Insert,
  widget: {
    id: 'wutiskill_cookie_consent',
    type: DIRECT_PLUGIN,
    priority: 101,
    RenderWidget: WutiCookieConsent,
  },
}
]);
    }
    if (process.env.APP_ID == 'gradebook') {
      addPlugins(config, 'footer_slot', [
{
  op: PLUGIN_OPERATIONS.Insert,
  widget: {
    id: 'wutiskill_theme_loader',
    type: DIRECT_PLUGIN,
    priority: 100,
    RenderWidget: WutiTenantThemeLoader,
  },
}
]);
      addPlugins(config, 'footer_slot', [
{
  op: PLUGIN_OPERATIONS.Insert,
  widget: {
    id: 'wutiskill_cookie_consent',
    type: DIRECT_PLUGIN,
    priority: 101,
    RenderWidget: WutiCookieConsent,
  },
}
]);
    }
    if (process.env.APP_ID == 'learner-dashboard') {
      addPlugins(config, 'widget_sidebar_slot', [
        {
            op: PLUGIN_OPERATIONS.Hide,
            widgetId: "default_contents"
        }]);
      addPlugins(config, 'footer_slot', [
{
  op: PLUGIN_OPERATIONS.Insert,
  widget: {
    id: 'wutiskill_theme_loader',
    type: DIRECT_PLUGIN,
    priority: 100,
    RenderWidget: WutiTenantThemeLoader,
  },
}
]);
      addPlugins(config, 'footer_slot', [
{
  op: PLUGIN_OPERATIONS.Insert,
  widget: {
    id: 'wutiskill_cookie_consent',
    type: DIRECT_PLUGIN,
    priority: 101,
    RenderWidget: WutiCookieConsent,
  },
}
]);
    }
    if (process.env.APP_ID == 'learning') {// wutiskill-ai: Import chat widget components for Learning MFE
const { AIChatTray } = await import('@wutiskill/ai-chat-widget');

// wutiskill-ai: MFE env config for Learning MFE
window.WUTISKILL_AI_CONFIG = {
  ENABLE: 'true',
  ENABLE_CHAT: 'true',
  LMS_BASE_URL: 'https://wutilms.com',
};
      addPlugins(config, 'org.openedx.frontend.layout.footer.v1', [
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'wutiskill_ai_chat_widget_learning',
                    priority: 50,
                    type: DIRECT_PLUGIN,
                    RenderWidget: AIChatTray,
                },
            }]);
      addPlugins(config, 'footer_slot', [
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'wutiskill_ai_chat_widget_learning',
                    priority: 50,
                    type: DIRECT_PLUGIN,
                    RenderWidget: AIChatTray,
                },
            }]);
      addPlugins(config, 'footer_slot', [
{
  op: PLUGIN_OPERATIONS.Insert,
  widget: {
    id: 'wutiskill_theme_loader',
    type: DIRECT_PLUGIN,
    priority: 100,
    RenderWidget: WutiTenantThemeLoader,
  },
}
]);
      addPlugins(config, 'footer_slot', [
{
  op: PLUGIN_OPERATIONS.Insert,
  widget: {
    id: 'wutiskill_cookie_consent',
    type: DIRECT_PLUGIN,
    priority: 101,
    RenderWidget: WutiCookieConsent,
  },
}
]);
    }
    if (process.env.APP_ID == 'ora-grading') {
    }
    if (process.env.APP_ID == 'profile') {
      addPlugins(config, 'footer_slot', [
{
  op: PLUGIN_OPERATIONS.Insert,
  widget: {
    id: 'wutiskill_theme_loader',
    type: DIRECT_PLUGIN,
    priority: 100,
    RenderWidget: WutiTenantThemeLoader,
  },
}
]);
      addPlugins(config, 'footer_slot', [
{
  op: PLUGIN_OPERATIONS.Insert,
  widget: {
    id: 'wutiskill_cookie_consent',
    type: DIRECT_PLUGIN,
    priority: 101,
    RenderWidget: WutiCookieConsent,
  },
}
]);
    }
    if (process.env.APP_ID == 'catalog') {
      addPlugins(config, 'footer_slot', [
{
  op: PLUGIN_OPERATIONS.Insert,
  widget: {
    id: 'wutiskill_theme_loader',
    type: DIRECT_PLUGIN,
    priority: 100,
    RenderWidget: WutiTenantThemeLoader,
  },
}
]);
      addPlugins(config, 'footer_slot', [
{
  op: PLUGIN_OPERATIONS.Insert,
  widget: {
    id: 'wutiskill_cookie_consent',
    type: DIRECT_PLUGIN,
    priority: 101,
    RenderWidget: WutiCookieConsent,
  },
}
]);
    }
  } catch (err) { console.error("env.config.jsx failed to apply: ", err);}

  return config;
}

export default setConfig;